import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import DOMPurify from 'dompurify';
import articles from './articles.json';
import { Analytics } from '@vercel/analytics/react';

const socialLinks = [{ icon: '@ayhdaaaa', url: 'https://x.com/ayhdaaaa' }];

// --- COMPONENTS ---

const Navbar = ({ theme, toggleTheme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="bg-transparent">
      {/* Container for logo and mobile hamburger */}
      <div className="flex justify-between items-center">
        <div
          className={`text-5xl font-bold ${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          }`}
        >
          <Link to="/">CERTARE</Link>
        </div>
        <div className="md:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`${
              theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
            } focus:outline-none`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={
                  isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16m-7 6h7'
                }
              ></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop nav links */}
      <div className="hidden md:flex flex-col mt-8 space-y-4">
        <Link
          to="/"
          className={`${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          } py-2 px-3 rounded-md transition-colors duration-200 ${
            location.pathname === '/'
              ? theme === 'light'
                ? 'bg-gray-400'
                : 'bg-gray-700'
              : theme === 'light'
              ? 'hover:bg-gray-100'
              : 'hover:bg-gray-800'
          }`}
        >
          home
        </Link>
        <Link
          to="/about"
          className={`${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          } py-2 px-3 rounded-md transition-colors duration-200 ${
            location.pathname === '/about'
              ? theme === 'light'
                ? 'bg-gray-400'
                : 'bg-gray-700'
              : theme === 'light'
              ? 'hover:bg-gray-100'
              : 'hover:bg-gray-800'
          }`}
        >
          about
        </Link>
      </div>

      {/* Mobile nav links */}
      {isMenuOpen && (
        <div className="md:hidden mt-4 flex flex-col space-y-2">
          <Link
            to="/"
            onClick={() => setIsMenuOpen(false)}
            className={`block ${
              theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
            } py-2 px-4 ${
              location.pathname === '/'
                ? theme === 'light'
                  ? 'bg-gray-400'
                  : 'bg-gray-700'
                : theme === 'light'
                ? 'hover:bg-gray-100'
                : 'hover:bg-gray-800'
            } rounded`}
          >
            Home
          </Link>
          <Link
            to="/about"
            onClick={() => setIsMenuOpen(false)}
            className={`block ${
              theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
            } py-2 px-4 ${
              location.pathname === '/about'
                ? theme === 'light'
                  ? 'bg-gray-400'
                  : 'bg-gray-700'
                : theme === 'light'
                ? 'hover:bg-gray-100'
                : 'hover:bg-gray-800'
            } rounded`}
          >
            About
          </Link>
        </div>
      )}
      <button
        onClick={toggleTheme}
        className={`mt-4 p-1 rounded-md text-2xl ${
          theme === 'light' ? 'bg-gray-0 text-white' : 'bg-gray-0 text-gray-800'
        }`}
      >
        {theme === 'light' ? (
          <img
            src="/icon/light-mode.png"
            alt="Light Mode"
            className="w-6 h-6"
          />
        ) : (
          <img src="/icon/dark-mode.png" alt="Dark Mode" className="w-6 h-6" />
        )}
      </button>
    </nav>
  );
};

const ArticleCard = ({ title, publicationDate, snippet, onClick, theme }) => (
  <div
    className="border-b border-gray-700 py-6 cursor-pointer"
    onClick={onClick}
  >
    <h3
      className={`text-2xl font-bold ${
        theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
      }`}
    >
      {title}
    </h3>
    <p
      className={`${
        theme === 'light' ? 'text-gray-700' : 'text-gray-300'
      } mt-2`}
    >
      {publicationDate}
    </p>
    <p
      className={`${
        theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
      } mt-4`}
    >
      {snippet}
    </p>
  </div>
);

const HomePage = ({ onArticleClick, theme }) => (
  <div className="container mx-auto px-4 py-8">
    <h2
      className={`text-3xl font-bold ${
        theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
      } border-b-2 border-gray-500 pb-2 inline-block`}
    >
      LATEST NOTES
    </h2>
    <div className="mt-8">
      {articles.map((article) => (
        <ArticleCard
          key={article.id}
          {...article}
          onClick={() => onArticleClick(article)}
          theme={theme}
        />
      ))}
    </div>
  </div>
);

const ArticlePage = ({ article, onBackClick, theme }) => {
  if (!article) {
    return null; // or a loading spinner, or a redirect
  }

  const sanitizedHTML = DOMPurify.sanitize(article.fullText, {
    ADD_TAGS: ['ul', 'li'],
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <button
        onClick={onBackClick}
        className={`${
          theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
        } mb-4`}
      >
        Back
      </button>
      <h2
        className={`text-4xl font-bold mb-4 ${
          theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
        }`}
      >
        {article.title}
      </h2>
      <p
        className={`${
          theme === 'light' ? 'text-gray-400' : 'text-gray-300'
        } mt-2`}
      >
        {article.publicationDate}
      </p>
      {article.musicFile && (
        <audio controls autoPlay loop controlsList="nodownload">
          <source src={article.musicFile} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      )}
      <div
        className={`${
          theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
        } text-lg leading-relaxed my-4 prose`}
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    </div>
  );
};

const AboutPage = ({ theme }) => (
  <div className="container mx-auto px-4 py-8">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
      <div className="md:col-span-1">
        <img
          src="/greekqueen-rmbg.png"
          alt="Writer's Profile"
          className="rounded-lg mx-auto md:mx-0"
        />
      </div>
      <div className="md:col-span-2">
        <h2
          className={`text-4xl font-bold mb-4 ${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          }`}
        >
          About Me
        </h2>
        <div
          className={`${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          } text-lg leading-relaxed mb-4`}
        >
          <p>
            Hi! Welcome!
          </p>
          <br />
          <p>
            Finally, I've decided to put some of my thoughts into writing, out in
            the open. People have unique abilities. Some are good at speaking the
            truth, while others are good at stirring emotions through poetic
            words. And yeah, of course, I'm neither of them, lol.
          </p>
          <br />
          <p>
            Anyway, I created this solely to reflect on what I've experienced
            throughout this adventure of life. I chose the name <b>Certare</b> for
            this space. It's a Latin word that means to strive, contend, and
            compete. It perfectly captures my belief that life is a game where
            the most important competition is against our former selves as we
            strive to live a life that is truly fulfilling.
          </p>
          <br />
          <p>
            Of course, some things are better left unsaid and will just remain in
            my private journal, but I think anything that I put here is worth
            sharing. I've always believed that it’s okay to be wrong about
            things in this world, but the key is to learn from those mistakes.
            It's true that to learn how to swim, you have to get in the water.
            But by studying it beforehand, at least you have an idea of how not
            to drown. Pay the cost of entry of being a beginner, and you will
            taste the sweet fruit of success.
          </p>
          <br />
          <p>
            If you want to discuss anything—ranging from how to swim the
            butterfly stroke, to why the Labubu doll is shaping social currency,
            or what will happen to the world in an era of post-modern AI
            robotics—just send me a message through the social media where you
            found this link. Or, if you want to remain anonymous, do it via this
            link: <a href="https://secreto.site/akmqw7" target="_blank" rel="noopener noreferrer">https://secreto.site/akmqw7</a>
          </p>
          <br />
          <p>
            So, let's grab some popcorn, turn off the TV, sit on the couch with
            me, and let's dive in together to win this game called life.
          </p>
          <br />
          <p>Cheers!</p>
        </div>
      </div>
    </div>
  </div>
);

const Footer = ({ theme }) => (
  <footer className="bg-transparent py-6 mt-12">
    <div className="container mx-auto text-center">
      <div className="flex justify-center space-x-6 mb-4">
        {socialLinks.map((link) => (
          <a
            key={link.icon}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${
              theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
            } hover:text-gray-400`}
          >
            {link.icon}
          </a>
        ))}
      </div>
      <p className={`${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
        &copy; 2025 Certare &mdash;{' '}
        <Link
          to="/about"
          className={`${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          } hover:underline`}
        >
          About
        </Link>
      </p>
    </div>
  </footer>
);

function App() {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [theme, setTheme] = useState('dark'); // Default theme is dark
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    navigate(`/article/${article.id}`);
  };

  const handleBackClick = () => {
    setSelectedArticle(null);
    navigate('/');
  };

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-300 ${
        theme === 'light'
          ? 'bg-custom-bg text-custom-font'
          : 'bg-dark-custom-bg text-dark-custom-font'
      }`}
      style={{ fontFamily: 'Inconsolata, monospace' }}
    >
      <div className="flex flex-col md:flex-row">
        <aside
          className={`md:min-h-screen ${
            theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
          } transition-all duration-300 ease-in-out overflow-hidden ${
            isDesktopSidebarOpen
              ? 'w-full md:w-1/4 p-8 border-r'
              : 'w-0 p-0 border-none'
          }`}
        >
          <Navbar theme={theme} toggleTheme={toggleTheme} />
          <div className="mt-8">
            {socialLinks.map((link) => (
              <div
                key={link.icon}
                className={`flex items-center space-x-2 ${
                  theme === 'light'
                    ? 'text-custom-font'
                    : 'text-dark-custom-font'
                } mb-4`}
              >
                <span>{link.icon}</span>
              </div>
            ))}
          </div>
        </aside>
        <main
          className={`w-full p-8 transition-all duration-300 ease-in-out ${
            isDesktopSidebarOpen ? 'md:w-3/4' : 'md:w-full'
          }`}
        >
          <button
            onClick={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)}
            className={`hidden md:block ${
              theme === 'light' ? 'text-custom-font' : 'text-dark-custom-font'
            } focus:outline-none mb-4`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={
                  isDesktopSidebarOpen
                    ? 'M6 18L18 6M6 6l12 12'
                    : 'M4 6h16M4 12h16m-7 6h7'
                }
              ></path>
            </svg>
          </button>
          <Routes>
            <Route
              path="/"
              element={
                <HomePage onArticleClick={handleArticleClick} theme={theme} />
              }
            />
            <Route path="/about" element={<AboutPage theme={theme} />} />
            <Route
              path="/article/:id"
              element={
                <ArticlePage
                  article={selectedArticle}
                  onBackClick={handleBackClick}
                  theme={theme}
                />
              }
            />
          </Routes>
          <Footer theme={theme} />
        </main>
      </div>
    </div>
  );
}

const AppWrapper = () => (
  <Router>
    <App />
    <Analytics />
  </Router>
);

export default AppWrapper;
